/**
 * useFormGeneration Hook
 *
 * React hook for generating pre-filled permit application forms.
 * Handles template loading, data gathering, and PDF generation.
 *
 * Part of Phase 4: Form Generation
 * Now uses Cloud Functions for PDF generation (server-side processing).
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db, functions } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';
import {
  buildDataSources,
  downloadPdf,
  resolveAllFields,
} from '@/lib/form-filler';
import {
  getFormTemplatesByJurisdiction,
  getFormTemplateById,
  getAllFormTemplates,
  isSupabaseConfigured,
} from '@/lib/supabase';
import type {
  FormTemplate,
  FormGenerationResult,
  FormDataSources,
  ResolvedFieldValue,
  AvailableFormTemplates,
  FormPermitType,
} from '@/types/jurisdiction';

// Cloud Function types
interface FillFormTemplateRequest {
  jobId: string;
  templateId: string;
  overrides?: Record<string, string>;
  includeWatermark?: boolean;
}

interface FillFormTemplateResponse {
  success: boolean;
  pdfBase64?: string;
  filename?: string;
  completeness?: number;
  fieldsNeedingReview?: string[];
  fieldsWithErrors?: string[];
  warnings?: string[];
  error?: string;
}

interface PreviewFormFieldsRequest {
  jobId: string;
  templateId: string;
  overrides?: Record<string, string>;
}

interface PreviewFormFieldsResponse {
  success: boolean;
  fields?: Array<{
    formField: string;
    value: string;
    confidence: 'high' | 'medium' | 'low';
    source: string;
    isValid: boolean;
    validationError?: string;
  }>;
  completeness?: number;
  error?: string;
}

export interface UseFormGenerationOptions {
  jobId: string;
}

export interface FormGenerationState {
  isLoading: boolean;
  isGenerating: boolean;
  error: string | null;
  result: FormGenerationResult | null;
  previewFields: ResolvedFieldValue[];
  dataSources: FormDataSources | null;
}

// Template cache to avoid repeated database calls
const templateCache = new Map<string, FormTemplate>();

export function useFormGeneration({ jobId }: UseFormGenerationOptions) {
  const { user } = useAuth();
  const [state, setState] = useState<FormGenerationState>({
    isLoading: false,
    isGenerating: false,
    error: null,
    result: null,
    previewFields: [],
    dataSources: null,
  });

  // Track all templates for getAllTemplates
  const [allTemplates, setAllTemplates] = useState<FormTemplate[]>([]);
  const templatesLoadedRef = useRef(false);

  // Load all templates once on mount (for getAllTemplates)
  useEffect(() => {
    if (!templatesLoadedRef.current && isSupabaseConfigured()) {
      templatesLoadedRef.current = true;
      getAllFormTemplates().then(({ data }) => {
        if (data.length > 0) {
          setAllTemplates(data);
          // Cache them
          data.forEach((t) => templateCache.set(t.id, t));
        }
      });
    }
  }, []);

  /**
   * Get available templates for a jurisdiction (from Supabase)
   */
  const getAvailableTemplates = useCallback(
    async (jurisdictionCode: string): Promise<AvailableFormTemplates> => {
      if (!isSupabaseConfigured()) {
        return {
          jurisdictionCode,
          jurisdictionName: jurisdictionCode,
          hasTemplates: false,
          templates: [],
          noTemplatesMessage: 'Supabase is not configured. Form templates are unavailable.',
        };
      }

      const { data: templates, error } = await getFormTemplatesByJurisdiction(jurisdictionCode);

      if (error) {
        console.error('Failed to fetch templates:', error);
        return {
          jurisdictionCode,
          jurisdictionName: jurisdictionCode,
          hasTemplates: false,
          templates: [],
          noTemplatesMessage: 'Failed to load form templates. Please try again.',
        };
      }

      if (templates.length === 0) {
        return {
          jurisdictionCode,
          jurisdictionName: jurisdictionCode,
          hasTemplates: false,
          templates: [],
          noTemplatesMessage: `Form templates for ${jurisdictionCode} are coming soon. Check back later!`,
        };
      }

      // Cache the templates
      templates.forEach((t) => templateCache.set(t.id, t));

      return {
        jurisdictionCode,
        jurisdictionName: templates[0]?.jurisdictionName || jurisdictionCode,
        hasTemplates: templates.length > 0,
        templates: templates.map((t) => ({
          id: t.id,
          permitType: t.permitType,
          formName: t.formName,
          version: t.version,
          lastUpdated: t.lastUpdated,
          estimatedCompleteness: 0, // Will be calculated when data is loaded
        })),
      };
    },
    []
  );

  /**
   * Load job and contractor data for form generation
   */
  const loadFormData = useCallback(async (): Promise<FormDataSources | null> => {
    if (!user || !jobId) return null;

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      // Load job data
      const jobRef = doc(db, 'contractors', user.uid, 'jobs', jobId);
      const jobSnap = await getDoc(jobRef);

      if (!jobSnap.exists()) {
        throw new Error('Job not found');
      }

      const jobData = jobSnap.data();

      // Load contractor profile data
      const contractorRef = doc(db, 'contractors', user.uid);
      const contractorSnap = await getDoc(contractorRef);
      const contractorData = contractorSnap.exists() ? contractorSnap.data() : {};

      // Get first license (for permit applications)
      const primaryLicense = contractorData.licenses?.[0] || {};

      // Get first insurance policy
      const primaryInsurance = contractorData.insurance?.[0] || {};

      // Build data sources
      const dataSources = buildDataSources(
        {
          ...jobData,
          // Flatten nested structures for easier path access
          address: jobData.address || jobData.jobAddress,
          city: jobData.city || (jobData.addressComponents?.city),
          state: jobData.state || (jobData.addressComponents?.state) || 'FL',
          zip: jobData.zip || (jobData.addressComponents?.zip),
        },
        {
          companyName: contractorData.company_name || contractorData.companyName,
          companyAddress: contractorData.company_address || contractorData.address,
          companyPhone: contractorData.company_phone || contractorData.phone,
          companyEmail: contractorData.company_email || contractorData.email,
          licenseNumber: primaryLicense.number || primaryLicense.licenseNumber,
          licenseType: primaryLicense.type || primaryLicense.licenseType,
          licenseExpiration: primaryLicense.expirationDate,
          qualifierName: primaryLicense.qualifierName,
          insuranceCarrier: primaryInsurance.carrier,
          insurancePolicyNumber: primaryInsurance.policyNumber,
          insuranceExpiration: primaryInsurance.expirationDate,
          // Add more contractor fields as needed
          ...contractorData,
        },
        {
          // Jurisdiction data would come from compliance check results
          name: jobData.jurisdictionName,
          code: jobData.jurisdictionCode,
        }
      );

      setState((prev) => ({ ...prev, isLoading: false, dataSources }));
      return dataSources;
    } catch (error) {
      console.error('Failed to load form data:', error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load data',
      }));
      return null;
    }
  }, [user, jobId]);

  /**
   * Get a template by ID (from cache or Supabase)
   */
  const fetchTemplate = useCallback(async (templateId: string): Promise<FormTemplate | null> => {
    // Check cache first
    if (templateCache.has(templateId)) {
      return templateCache.get(templateId)!;
    }

    // Fetch from Supabase
    if (!isSupabaseConfigured()) {
      return null;
    }

    const { data, error } = await getFormTemplateById(templateId);
    if (error || !data) {
      console.error('Failed to fetch template:', error);
      return null;
    }

    // Cache for future use
    templateCache.set(templateId, data);
    return data;
  }, []);

  /**
   * Preview form fields without generating PDF
   * Uses Cloud Function for server-side field resolution
   */
  const previewForm = useCallback(
    async (templateId: string, overrides?: Record<string, string>): Promise<ResolvedFieldValue[]> => {
      const template = await fetchTemplate(templateId);
      if (!template) {
        setState((prev) => ({ ...prev, error: 'Template not found' }));
        return [];
      }

      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        // Call Cloud Function for server-side field resolution
        const previewFormFields = httpsCallable<PreviewFormFieldsRequest, PreviewFormFieldsResponse>(
          functions,
          'previewFormFields',
          { timeout: 60000 }
        );

        const response = await previewFormFields({
          jobId,
          templateId,
          overrides,
        });

        const data = response.data;

        if (!data.success || !data.fields) {
          throw new Error(data.error || 'Failed to preview form fields');
        }

        // Convert to ResolvedFieldValue format
        const fields: ResolvedFieldValue[] = data.fields.map((f) => ({
          formField: f.formField,
          value: f.value,
          originalValue: f.value,
          dataPath: '',
          usedDefault: false,
          confidence: f.confidence,
          source: f.source as 'job' | 'contractor' | 'jurisdiction' | 'generated' | 'default' | 'user_override',
          isValid: f.isValid,
          validationError: f.validationError,
        }));

        setState((prev) => ({ ...prev, isLoading: false, previewFields: fields }));
        return fields;
      } catch (error) {
        console.error('Preview failed:', error);

        // Fallback to client-side preview if Cloud Function fails
        console.log('Falling back to client-side preview...');
        let dataSources = state.dataSources;
        if (!dataSources) {
          dataSources = await loadFormData();
          if (!dataSources) {
            setState((prev) => ({ ...prev, isLoading: false }));
            return [];
          }
        }

        const fields = resolveAllFields(template, dataSources, overrides);
        setState((prev) => ({ ...prev, isLoading: false, previewFields: fields }));
        return fields;
      }
    },
    [jobId, state.dataSources, loadFormData, fetchTemplate]
  );

  /**
   * Generate the filled PDF form using Cloud Function
   */
  const generateForm = useCallback(
    async (
      templateId: string,
      options: {
        overrides?: Record<string, string>;
        includeWatermark?: boolean;
        highlightMissing?: boolean;
      } = {}
    ): Promise<FormGenerationResult | null> => {
      const template = await fetchTemplate(templateId);
      if (!template) {
        setState((prev) => ({ ...prev, error: 'Template not found' }));
        return null;
      }

      setState((prev) => ({ ...prev, isGenerating: true, error: null }));

      try {
        // Call Cloud Function for server-side PDF generation
        const fillFormTemplate = httpsCallable<FillFormTemplateRequest, FillFormTemplateResponse>(
          functions,
          'fillFormTemplate',
          { timeout: 120000 } // 2 minute timeout
        );

        const response = await fillFormTemplate({
          jobId,
          templateId,
          overrides: options.overrides,
          includeWatermark: options.includeWatermark ?? true,
        });

        const data = response.data;

        if (!data.success || !data.pdfBase64) {
          throw new Error(data.error || 'Failed to generate form');
        }

        // Convert base64 to Blob
        const binaryString = atob(data.pdfBase64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const pdfBlob = new Blob([bytes], { type: 'application/pdf' });

        const result: FormGenerationResult = {
          success: true,
          templateId,
          fields: state.previewFields, // Use preview fields
          fieldsNeedingReview: data.fieldsNeedingReview || [],
          fieldsWithErrors: data.fieldsWithErrors || [],
          completeness: data.completeness || 0,
          pdfBlob,
          warnings: data.warnings || [],
        };

        setState((prev) => ({
          ...prev,
          isGenerating: false,
          result,
        }));

        return result;
      } catch (error) {
        console.error('Form generation failed:', error);
        setState((prev) => ({
          ...prev,
          isGenerating: false,
          error: error instanceof Error ? error.message : 'Form generation failed',
        }));
        return null;
      }
    },
    [jobId, state.previewFields, fetchTemplate]
  );

  /**
   * Download the generated PDF
   */
  const downloadForm = useCallback(
    (filename?: string) => {
      if (!state.result?.pdfBlob) {
        console.error('No PDF to download');
        return;
      }

      const defaultFilename = `permit-application-${jobId}-${Date.now()}.pdf`;
      downloadPdf(state.result.pdfBlob, filename || defaultFilename);
    },
    [state.result, jobId]
  );

  /**
   * Get template by ID (sync version for cached templates)
   */
  const getTemplate = useCallback((templateId: string): FormTemplate | null => {
    return templateCache.get(templateId) || null;
  }, []);

  /**
   * Get all registered templates (from state, loaded on mount)
   */
  const getAllTemplates = useMemo(() => {
    return allTemplates;
  }, [allTemplates]);

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    setState({
      isLoading: false,
      isGenerating: false,
      error: null,
      result: null,
      previewFields: [],
      dataSources: null,
    });
  }, []);

  return {
    // State
    ...state,

    // Actions
    getAvailableTemplates,
    loadFormData,
    previewForm,
    generateForm,
    downloadForm,
    getTemplate,
    reset,

    // Data
    getAllTemplates,
  };
}

export default useFormGeneration;
